// src/components/auth/ProtectedRoute.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { readLastRole } from "@/lib/roleCache";
import supabase from "@/services/supabaseClient";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";

const INSTITUTIONAL_ROLES = new Set([
  "super_admin",
  "moh_admin",
  "institution_admin",
  "facility_admin",
]);

const ADMIN_AREA_ROLES = new Set(["super_admin", "admin", "support", "kyc_reviewer"]);
const INTERNAL_ADMIN_EMAIL_DOMAIN = "afrimed.tech";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isInternalAdminEmail(value = "") {
  const email = normalizeEmail(value);
  return !!email && email.endsWith(`@${INTERNAL_ADMIN_EMAIL_DOMAIN}`);
}

function normalizeRole(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function hasAdminRoleInValues(values = [], allowedRoles = ADMIN_AREA_ROLES) {
  return values.map(normalizeRole).some((role) => allowedRoles.has(role));
}

function AdminAccessDenied() {
  return (
    <div className="auth-bg min-h-[100dvh] grid place-items-center px-4 text-center">
      <div className="max-w-[380px] space-y-2">
        <img
          src="/assets/images/afrimed_logo_stack.svg"
          alt="AfriMed"
          className="mx-auto mb-4 w-28"
        />
        <h1 className="text-title-large text-on-surface">Admin access unavailable</h1>
        <p className="text-body-medium text-on-surface-var">
          We could not open admin access for this account. Contact the AfriMed™ platform administrator.
        </p>
      </div>
    </div>
  );
}

function getDevBypassRole() {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");
    const bypassEnabled =
      !!import.meta.env.DEV &&
      isLocal &&
      String(import.meta.env.VITE_BYPASS_AUTH || "") === "true";
    if (!bypassEnabled) return "";
    return String(import.meta.env.VITE_DEV_ROLE || "patient").trim().toLowerCase();
  } catch {
    return "";
  }
}

/**
 * ProtectedRoute
 * - Waits for AuthProvider to finish hydrating (loading).
 * - Supports dev bypass via VITE_BYPASS_AUTH (localhost only).
 * - Optional "allowed" roles array, compared to resolved role.
 */
export default function ProtectedRoute({
  allowed,
  children,
  loginPath = "/login",
  requireProfile = true,
}) {
  const { user, profile, loading, profileResolved } = useAuth();
  const location = useLocation();
  const allowedLower = Array.isArray(allowed)
    ? allowed.map((r) => normalizeRole(r)).filter(Boolean)
    : [];
  const allowedAdminRoles = React.useMemo(
    () => new Set(allowedLower.filter((role) => ADMIN_AREA_ROLES.has(role))),
    [allowedLower.join("|")]
  );
  const isAdminRoute = String(location.pathname || "").startsWith("/admin");
  const adminRouteRequested = isAdminRoute && allowedAdminRoles.size > 0;
  const immediateAdminEntitlement = hasAdminRoleInValues(
    [
      profile?.role,
      user?.app_metadata?.role,
    ],
    allowedAdminRoles.size ? allowedAdminRoles : ADMIN_AREA_ROLES,
  );
  const [adminEntitlementState, setAdminEntitlementState] = React.useState({
    key: "",
    allowed: null,
  });

  React.useEffect(() => {
    const uid = String(user?.id || "").trim();
    const key = `${uid}:${location.pathname}:${allowedLower.join(",")}`;
    if (!adminRouteRequested || !uid || immediateAdminEntitlement) {
      setAdminEntitlementState({ key, allowed: immediateAdminEntitlement ? true : null });
      return undefined;
    }

    let alive = true;
    setAdminEntitlementState({ key, allowed: null });

    (async () => {
      try {
        const [rolesResult, profileResult] = await Promise.all([
          supabase.from("user_roles").select("role").eq("user_id", uid),
          supabase.from("profiles").select("role").eq("user_id", uid).maybeSingle(),
        ]);
        if (!alive) return;
        const roleRows = Array.isArray(rolesResult?.data) ? rolesResult.data : [];
        const roleValues = [
          ...roleRows.map((row) => row?.role),
          profileResult?.data?.role,
                  user?.app_metadata?.role,
        ];
        setAdminEntitlementState({
          key,
          allowed: hasAdminRoleInValues(roleValues, allowedAdminRoles),
        });
      } catch {
        if (!alive) return;
        setAdminEntitlementState({ key, allowed: false });
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    adminRouteRequested,
    allowedLower.join(","),
    immediateAdminEntitlement,
    location.pathname,
    user?.app_metadata?.role,
    user?.id,
    user?.user_metadata?.primary_role,
    user?.user_metadata?.role,
  ]);

  // 1) Dev bypass is allowed on localhost, but admin/institution dashboards
  // need an explicit matching VITE_DEV_ROLE so they do not silently open.
  const bypassRole = getDevBypassRole();
  if (bypassRole) {
    const path = String(location.pathname || "");
    const isInstitutionRoute = path.startsWith("/institutions");
    const isAdminRoute = path.startsWith("/admin");
    const canBypassInstitution = isInstitutionRoute && INSTITUTIONAL_ROLES.has(bypassRole);
    const canBypassAdmin = isAdminRoute && ADMIN_AREA_ROLES.has(bypassRole);

    if (!isInstitutionRoute && !isAdminRoute) return children;
    if (canBypassInstitution || canBypassAdmin) {
      // eslint-disable-next-line no-console
      console.info(`[ProtectedRoute] DEV auth bypass active for ${path} as ${bypassRole}.`);
      return children;
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[ProtectedRoute] DEV auth bypass blocked for ${path}; set VITE_DEV_ROLE to an explicit matching role.`
    );
    const params = new URLSearchParams();
    const fullPath = `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
    if (fullPath.startsWith("/") && !fullPath.startsWith("//")) params.set("next", fullPath);
    return <Navigate to={params.toString() ? `/login?${params.toString()}` : "/login"} replace />;
  }

  // 2) On background auth refreshes, keep the current protected UI mounted
  // if we already have a signed-in user. Pharmacy routing is resolved by
  // PharmacyProtectedRoute so it can read fresh owner/member state after launch.
  if (loading && !user?.id) return <RouteFallback label="Checking session..." />;
  if (requireProfile && profileResolved === false && (!user?.id || !profile)) {
    return <RouteFallback label="Loading AfriMed..." />;
  }
  if (loading && user?.id && !profile) return <RouteFallback label="Loading AfriMed™..." />;

  // 3) No session → go to login, remember where we came from
  if (!user?.id) {
    if (loginPath === "/login") {
      const fullPath = `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
      const params = new URLSearchParams();
      if (fullPath.startsWith("/") && !fullPath.startsWith("//")) {
        params.set("next", fullPath);
      }
      const target = params.toString() ? `/login?${params.toString()}` : "/login";
      return <Navigate to={target} replace state={{ from: location }} />;
    }
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  // ---- Resolve role safely (dual-role aware) -----------------------------
  const hasDoctor = !!profile?.hasDoctor;
  const hasPatient = !!profile?.hasPatient;

  // Prefer explicit profile.role (AuthContext should set it),
  // then last_role, then doctor-first rule if only one exists.
  let role = String(profile?.role || "").toLowerCase();
  const knownRoles = new Set([
    "doctor",
    "patient",
    "admin",
    "support",
    "kyc_reviewer",
    "pharmacy",
    "practice_team",
    "super_admin",
    "moh_admin",
    "institution_admin",
    "facility_admin",
  ]);

  if (!knownRoles.has(role)) {
      const last = readLastRole(user?.id);
      if (last) role = last;
      else if (hasDoctor && !hasPatient) role = "doctor";
      else if (!hasDoctor && hasPatient) role = "patient";
      else role = ""; // dual-role + no last_role → chooser required
  }

  const needsChooser = hasDoctor && hasPatient && !role;

  // 4) Onboarding Guard:
  // If they try to go BACK to onboarding once done, send them to a safe destination.
  if (profile?.onboarding_completed && location.pathname.startsWith("/onboarding")) {
    if (needsChooser) return <Navigate to="/choose-role" replace />;

    const target =
      role === "doctor"
        ? "/home/doctor"
        : role === "patient"
          ? "/home/patient"
          : role === "pharmacy"
            ? "/pharmacy/dashboard"
            : role === "super_admin" ||
                role === "moh_admin" ||
                role === "institution_admin" ||
                role === "facility_admin"
              ? "/institutions/home"
            : "/admin/kpi";
    return <Navigate to={target} replace />;
  }

  // 5) Role check (only if "allowed" provided)
  if (Array.isArray(allowed) && allowed.length > 0) {
    // If dual-role needs chooser, go there (don’t dump them into onboarding)
    if (needsChooser) {
      return <Navigate to="/choose-role" replace />;
    }

    // If role still unknown for any reason, safest recover path is /home (which should gate again)
    if (!knownRoles.has(role)) {
      return <Navigate to="/home" replace />;
    }

    if (adminRouteRequested && !isInternalAdminEmail(user?.email || profile?.email)) {
      const lastRole = readLastRole(user?.id);
      if (role === "pharmacy" || lastRole === "pharmacy") {
        return <Navigate to="/pharmacy/dashboard" replace />;
      }
      return <Navigate to="/home" replace />;
    }

    if (adminRouteRequested && !allowedLower.includes(role)) {
      const expectedKey = `${String(user?.id || "").trim()}:${location.pathname}:${allowedLower.join(",")}`;
      if (adminEntitlementState.key !== expectedKey || adminEntitlementState.allowed === null) {
        return <RouteFallback label="Checking admin access..." />;
      }
      if (adminEntitlementState.allowed === true) {
        return children;
      }
      return <AdminAccessDenied />;
    }

    if (!allowedLower.includes(role)) {
      if (allowedLower.includes("admin") && loginPath === "/admin") {
        return <Navigate to={loginPath} replace state={{ from: location }} />;
      }
      // If logged in but wrong role, go somewhere safe.
      if (role === "admin") {
        return <Navigate to="/admin/kpi" replace />;
      }
      if (role === "super_admin") {
        return <Navigate to="/admin/kpi" replace />;
      }
      if (role === "support") {
        return <Navigate to="/admin/users" replace />;
      }
      if (role === "kyc_reviewer") {
        return <Navigate to="/admin/kyc" replace />;
      }
      if (role === "pharmacy") {
        return (
          <Navigate
            to={profile?.onboarding_completed ? "/pharmacy/dashboard" : "/onboarding/pharmacy"}
            replace
          />
        );
      }
      if (role === "practice_team") {
        return <Navigate to="/practice-team/welcome" replace />;
      }
      if (
        role === "super_admin" ||
        role === "moh_admin" ||
        role === "institution_admin" ||
        role === "facility_admin"
      ) {
        return <Navigate to="/institutions/home" replace />;
      }
      return <Navigate to="/home" replace />;
    }
  }

  // ✅ All good
  return children;
}
